import { useState, useCallback } from 'react';
import { Gallery } from '../api/types';
import { PhotoStreamClient } from '../api/PhotoStreamClient';

interface UseGalleriesReturn {
  galleries: Gallery[];
  loading: boolean;
  error: string | null;
  loadGalleries: () => Promise<void>;
  createGallery: (name: string, description: string) => Promise<void>;
}

export const useGalleries = (apiClient: PhotoStreamClient): UseGalleriesReturn => {
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadGalleries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const galleryList = await apiClient.listGalleries();
      setGalleries(galleryList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load galleries');
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  const createGallery = useCallback(async (name: string, description: string) => {
    try {
      setLoading(true);
      setError(null);
      await apiClient.createGallery(name, description);
      await loadGalleries();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create gallery');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [apiClient, loadGalleries]);

  return {
    galleries,
    loading,
    error,
    loadGalleries,
    createGallery,
  };
};
